// Stripe Payment Service for Toefetti.ai
// Handles subscriptions, payments, and premium features

import { loadStripe, Stripe } from '@stripe/stripe-js'
import { supabase } from '../lib/supabase'

// Stripe configuration
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RhIKlGLiG8YFB1K5QL3hOuPB6tvHVL3NHhtUYQQtiSvDpyoe8JkAbt1WkFkTGKI7riDiNKK0psn8X0eDcoxum3I00NcE4PDXl'

// Subscription tiers for Toefetti.ai
export interface SubscriptionTier {
  id: string
  name: string
  price: number
  priceId: string
  features: string[]
  maxGenerationsPerMonth: number
  priority: boolean
}

// Credit packages for one-time purchases
export interface CreditPackage {
  id: string
  name: string
  credits: number
  price: number
  originalPrice?: number
  savings?: number
  savingsPercent?: number
  priceId: string
  popular?: boolean
  bestValue?: boolean
}

export const SUBSCRIPTION_TIERS: SubscriptionTier[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    priceId: '',
    features: [
      '5 AI generations per month',
      'Basic filters',
      'Standard quality images',
      'Community support'
    ],
    maxGenerationsPerMonth: 5,
    priority: false
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 9.99,
    priceId: 'price_premium_monthly', // Replace with actual Stripe price ID
    features: [
      '100 AI generations per month',
      'All premium filters',
      'HD quality images',
      'Priority generation queue',
      'Download in multiple formats',
      'Email support'
    ],
    maxGenerationsPerMonth: 100,
    priority: true
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 19.99,
    priceId: 'price_pro_monthly', // Replace with actual Stripe price ID
    features: [
      'Unlimited AI generations',
      'All filters + exclusive ones',
      'Ultra HD quality images',
      'Highest priority queue',
      'Commercial usage rights',
      'API access',
      'Priority support'
    ],
    maxGenerationsPerMonth: -1, // Unlimited
    priority: true
  }
]

// Extended subscription options with progressive discounts
export const EXTENDED_SUBSCRIPTION_OPTIONS = [
  // Monthly subscriptions
  {
    id: '1',
    name: '1 Month Premium',
    price: 9.99,
    priceId: 'price_premium_1month',
    duration: 1,
    type: 'subscription'
  },
  {
    id: '3',
    name: '3 Month Premium',
    price: 24.99,
    originalPrice: 29.97,
    savings: 4.98,
    savingsPercent: 17,
    priceId: 'price_premium_3month',
    duration: 3,
    type: 'subscription',
    popular: true
  },
  {
    id: '6',
    name: '6 Month Premium',
    price: 44.99,
    originalPrice: 59.94,
    savings: 14.95,
    savingsPercent: 25,
    priceId: 'price_premium_6month',
    duration: 6,
    type: 'subscription'
  },
  {
    id: '12',
    name: '1 Year Premium',
    price: 79.99,
    originalPrice: 119.88,
    savings: 39.89,
    savingsPercent: 33,
    priceId: 'price_premium_12month',
    duration: 12,
    type: 'subscription',
    bestValue: true
  }
]

// Credit packages with progressive discounts
export const CREDIT_PACKAGES: CreditPackage[] = [
  {
    id: 'credits_10',
    name: '10 Image Credits',
    credits: 10,
    price: 4.99,
    originalPrice: 5.99,
    savings: 1.00,
    savingsPercent: 17,
    priceId: 'price_credits_10'
  },
  {
    id: 'credits_25',
    name: '25 Image Credits',
    credits: 25,
    price: 9.99,
    originalPrice: 12.49,
    savings: 2.50,
    savingsPercent: 20,
    priceId: 'price_credits_25'
  },
  {
    id: 'credits_50',
    name: '50 Image Credits',
    credits: 50,
    price: 17.99,
    originalPrice: 24.99,
    savings: 7.00,
    savingsPercent: 28,
    priceId: 'price_credits_50',
    popular: true
  },
  {
    id: 'credits_100',
    name: '100 Image Credits',
    credits: 100,
    price: 29.99,
    originalPrice: 49.99,
    savings: 20.00,
    savingsPercent: 40,
    priceId: 'price_credits_100',
    bestValue: true
  }
]

// Initialize Stripe
let stripePromise: Promise<Stripe | null>

export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)
  }
  return stripePromise
}

// Get Stripe secret key from Supabase Edge Function
export const getStripeSecretKey = async (): Promise<string | null> => {
  try {
    console.log('🔑 Retrieving Stripe API key from Supabase...')
    
    const { data: { session }, error: sessionError } = await supabase.auth.getSession()
    
    if (sessionError || !session) {
      console.warn('No active session found for Stripe API key retrieval')
      return null
    }
    
    const { data, error } = await supabase.functions.invoke('get-stripe-key', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({
        userId: session.user.id
      })
    })

    if (error) {
      console.error('❌ Error calling Stripe key Edge Function:', error)
      return null
    }

    if (data?.error) {
      console.error('❌ Stripe Edge Function returned error:', data.error)
      return null
    }

    if (data?.apiKey) {
      console.log('✅ Successfully retrieved Stripe API key')
      return data.apiKey
    } else {
      console.warn('❌ Stripe API key not found in response')
      return null
    }
  } catch (error) {
    console.error('❌ Error retrieving Stripe API key:', error)
    return null
  }
}

// Create customer portal session for subscription management
export const createCustomerPortalSession = async (customerId: string, returnUrl: string) => {
  try {
    const { data, error } = await supabase.functions.invoke('create-customer-portal-session', {
      body: {
        customerId,
        returnUrl
      }
    })

    if (error) {
      console.error('Error creating customer portal session:', error)
      throw error
    }

    return data
  } catch (error) {
    console.error('Error creating customer portal session:', error)
    throw error
  }
}

// Create checkout session for subscription
export const createCheckoutSession = async (
  priceId: string,
  userId: string,
  successUrl: string,
  cancelUrl: string,
  mode: 'subscription' | 'payment' = 'subscription'
) => {
  try {
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        priceId,
        userId,
        successUrl,
        cancelUrl,
        mode
      }
    })

    if (error) {
      console.error('Error creating checkout session:', error)
      throw error
    }

    return data
  } catch (error) {
    console.error('Error creating checkout session:', error)
    throw error
  }
}

// Create checkout session for credit purchase
export const createCreditCheckoutSession = async (
  creditPackageId: string,
  userId: string,
  successUrl: string,
  cancelUrl: string
) => {
  try {
    const creditPackage = CREDIT_PACKAGES.find(pkg => pkg.id === creditPackageId)
    if (!creditPackage) {
      throw new Error('Invalid credit package')
    }

    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        priceId: creditPackage.priceId,
        userId,
        successUrl,
        cancelUrl,
        mode: 'payment',
        metadata: {
          type: 'credits',
          credits: creditPackage.credits.toString(),
          packageId: creditPackageId
        }
      }
    })

    if (error) {
      console.error('Error creating credit checkout session:', error)
      throw error
    }

    return data
  } catch (error) {
    console.error('Error creating credit checkout session:', error)
    throw error
  }
}

// Get user's subscription status
export const getUserSubscription = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .single()

    if (error && (error as any).code !== 'PGRST116') { // Not found error is OK
      console.error('Error fetching user subscription:', error)
      return null
    }

    return data
  } catch (error) {
    console.error('Error fetching user subscription:', error)
    return null
  }
}

// Get user's credit balance
export const getUserCredits = async (userId: string): Promise<number> => {
  try {
    const { data, error } = await supabase
      .from('user_credits')
      .select('credits')
      .eq('user_id', userId)
      .single()

    if (error && error.message !== 'No rows found') {
      console.error('Error fetching user credits:', error)
      return 0
    }

    return data?.credits || 0
  } catch (error) {
    console.error('Error fetching user credits:', error)
    return 0
  }
}

// Add credits to user account
export const addUserCredits = async (userId: string, credits: number): Promise<boolean> => {
  try {
    const { error } = await supabase.rpc('add_user_credits', {
      user_id_param: userId,
      credits_param: credits
    })

    if (error) {
      console.error('Error adding user credits:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('Error adding user credits:', error)
    return false
  }
}

// Use credits for generation
export const useUserCredits = async (userId: string, credits: number = 1): Promise<boolean> => {
  try {
    const { error } = await supabase.rpc('use_user_credits', {
      user_id_param: userId,
      credits_param: credits
    })

    if (error) {
      console.error('Error using user credits:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('Error using user credits:', error)
    return false
  }
}

// Check if user can generate more images
export const canUserGenerate = async (userId: string): Promise<boolean> => {
  try {
    const subscription = await getUserSubscription(userId)
    
    if (!subscription) {
      // Free tier user, check monthly limit and credits
      const [monthlyUsed, credits] = await Promise.all([
        getMonthlyGenerationCount(userId),
        getUserCredits(userId)
      ])
      
      const freeTier = SUBSCRIPTION_TIERS.find(t => t.id === 'free')
      const monthlyLimit = freeTier?.maxGenerationsPerMonth || 5
      
      // Can generate if within monthly limit OR has credits
      return monthlyUsed < monthlyLimit || credits > 0
    }
    
    // Premium/Pro user with active subscription
    if (subscription.status === 'active') {
      const tier = SUBSCRIPTION_TIERS.find(t => t.id === subscription.tier)
      
      if (tier?.maxGenerationsPerMonth === -1) {
        return true // Unlimited
      }
      
      // Check monthly limit for premium tier
      const monthlyUsed = await getMonthlyGenerationCount(userId)
      return monthlyUsed < (tier?.maxGenerationsPerMonth || 100)
    }
    
    // Inactive subscription, check credits
    const credits = await getUserCredits(userId)
    return credits > 0
  } catch (error) {
    console.error('Error checking if user can generate:', error)
    return false
  }
}

// Get monthly generation count
const getMonthlyGenerationCount = async (userId: string): Promise<number> => {
  const startOfMonth = new Date()
  startOfMonth.setDate(1)
  startOfMonth.setHours(0, 0, 0, 0)
  
  const { count, error } = await supabase
    .from('images')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', startOfMonth.toISOString())
  
  if (error) {
    console.error('Error checking generation count:', error)
    return 0
  }
  
  return count || 0
}

// Get user's remaining generations for the month
export const getUserRemainingGenerations = async (userId: string): Promise<number> => {
  try {
    const subscription = await getUserSubscription(userId)
    const credits = await getUserCredits(userId)
    
    const monthlyUsed = await getMonthlyGenerationCount(userId)
    
    if (!subscription || subscription.status !== 'active') {
      // Free tier
      const freeTier = SUBSCRIPTION_TIERS.find(t => t.id === 'free')
      const monthlyRemaining = Math.max(0, (freeTier?.maxGenerationsPerMonth || 5) - monthlyUsed)
      
      // Return monthly remaining + credits
      return monthlyRemaining + credits
    }
    
    // Premium/Pro tier
    const tier = SUBSCRIPTION_TIERS.find(t => t.id === subscription.tier)
    
    if (tier?.maxGenerationsPerMonth === -1) {
      return 999999 // Unlimited
    }
    
    return Math.max(0, (tier?.maxGenerationsPerMonth || 100) - monthlyUsed)
  } catch (error) {
    console.error('Error getting remaining generations:', error)
    return 0
  }
}

// Enhanced upgrade flow helper for subscriptions
export const initiateUpgrade = async (tierId: string, userId: string) => {
  try {
    let priceId: string
    
    // Handle extended subscription options
    const extendedOption = EXTENDED_SUBSCRIPTION_OPTIONS.find(opt => opt.id === tierId)
    if (extendedOption) {
      priceId = extendedOption.priceId
    } else {
      // Fallback to basic tiers
      const tier = SUBSCRIPTION_TIERS.find(t => t.id === tierId)
      if (!tier || tier.id === 'free') {
        throw new Error('Invalid subscription tier')
      }
      priceId = tier.priceId
    }
    
    const stripe = await getStripe()
    if (!stripe) {
      throw new Error('Stripe not initialized')
    }
    
    const successUrl = `${window.location.origin}/subscription/success`
    const cancelUrl = `${window.location.origin}/subscription/cancel`
    
    const session = await createCheckoutSession(
      priceId,
      userId,
      successUrl,
      cancelUrl,
      'subscription'
    )
    
    const result = await stripe.redirectToCheckout({
      sessionId: session.sessionId
    })
    
    if (result.error) {
      console.error('Stripe checkout error:', result.error)
      throw result.error
    }
  } catch (error) {
    console.error('Error initiating upgrade:', error)
    throw error
  }
}

// Credit purchase flow helper
export const initiateCreditPurchase = async (creditPackageId: string, userId: string) => {
  try {
    const stripe = await getStripe()
    if (!stripe) {
      throw new Error('Stripe not initialized')
    }
    
    const successUrl = `${window.location.origin}/credits/success`
    const cancelUrl = `${window.location.origin}/credits/cancel`
    
    const session = await createCreditCheckoutSession(
      creditPackageId,
      userId,
      successUrl,
      cancelUrl
    )
    
    const result = await stripe.redirectToCheckout({
      sessionId: session.sessionId
    })
    
    if (result.error) {
      console.error('Stripe checkout error:', result.error)
      throw result.error
    }
  } catch (error) {
    console.error('Error initiating credit purchase:', error)
    throw error
  }
}

// Manage subscription helper
export const manageSubscription = async (customerId: string) => {
  try {
    const returnUrl = window.location.origin
    
    const session = await createCustomerPortalSession(customerId, returnUrl)
    
    window.location.href = session.url
  } catch (error) {
    console.error('Error managing subscription:', error)
    throw error
  }
}

export default {
  getStripe,
  createCheckoutSession,
  createCreditCheckoutSession,
  getUserSubscription,
  getUserCredits,
  addUserCredits,
  useUserCredits,
  canUserGenerate,
  getUserRemainingGenerations,
  initiateUpgrade,
  initiateCreditPurchase,
  manageSubscription,
  SUBSCRIPTION_TIERS,
  EXTENDED_SUBSCRIPTION_OPTIONS,
  CREDIT_PACKAGES
} 